import { useEffect, useRef, useState } from 'react';
import { guideApi } from '@/lib/guideApi';

/**
 * Loads the DB-backed sidebar navigation for a guide type (docs | learn).
 *
 * Returns `{ sections, loading, error }`. `sections` is empty until the fetch
 * resolves. Loading/error states do not substitute a shipped content catalog.
 *
 * Shape of each section matches `buildGuideNavigation` on the backend:
 *   { id, title, slug, order, items: [{ slug, label, icon, description, href, navOrder }] }
 *
 * @param {'docs'|'learn'} guideType
 */
export default function useGuideNavigation(guideType) {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const cacheKey = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const type = String(guideType || '').toLowerCase();
    cacheKey.current = type;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const fetched = await guideApi.getNavigation(type);
        if (cancelled || cacheKey.current !== type) return;
        setSections(Array.isArray(fetched) ? fetched : []);
      } catch (err) {
        if (cancelled) return;
        setError(err);
        setSections([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [guideType]);

  return { sections, loading, error };
}
